import UpdateBeneficiaryStaffMembers from '@Application/Beneficiaries/Beneficiaries/Commands/UpdateBeneficiaryStaffMembers';
import updateBeneficiaryStaffMembers from '@Application/Beneficiaries/Beneficiaries/updateBeneficiaryStaffMembers';
import FetchStaffMembers from '@Application/Facilities/StaffMembers/StaffMembers/Queries/FetchStaffMembers';
import fetchStaffMembers from '@Application/Facilities/StaffMembers/StaffMembers/fetchStaffMembers';
import ErrorState from '@Guidelines/Component/ErrorState';
import { theme } from '@Guidelines/Theme/theme';
import ignoreHandledError from '@UI/Helpers/ignoreHandledError';
import useCurrentOrganization from '@UI/Providers/CurrentOrganizationProvider/useCurrentOrganization';
import type BeneficiaryView from '@UI/Store/Beneficiaries/Views/BeneficiaryView';
import type StaffMemberView from '@UI/Store/StaffMembers/Views/StaffMemberView';
import { css } from '@emotion/react';
import * as React from 'react';
import { useDispatch, useStoreQuery } from '@verclam/synapse';
import { Button, Dropdown, Form, Message, Skeleton } from '@verclam/ui-kit';
import { fieldMarginStyles } from '@Guidelines/Theme/commonStyles';

const panelStyle = css`
	display: flex;
	flex-direction: column;
	gap: 0.875rem;
	padding: 1.25rem;
	border-radius: ${theme.radii.xlarge};
	background-color: ${theme.colors.surface};
	border: 1.5px solid ${theme.colors.border};
`;

const eyebrowStyle = css`
	${theme.typography.eyebrow}
	text-transform: uppercase;
	color: ${theme.colors.inkMuted};
`;

const formStyle = css`
	display: flex;
	flex-direction: column;
	gap: 1rem;
`;

const actionsStyle = css`
	display: flex;
	justify-content: flex-end;
`;

type StaffMemberOption = {
	readonly id: number | null;
	readonly label: string;
};

const NONE_OPTION: StaffMemberOption = { id: null, label: 'Aucun' };

// The current value is always present, even absent from `candidates`: a member rattached to
// this dossier but outside the current facility still needs an option to render as selected. Two
// distinct cases: known to the organization's full roster (`currentMember`) → its real name; unknown
// to the front at all (never received outside its read scope) → a degraded label carrying the id.
// Saving the OTHER field must never silently detach this one.
const buildStaffMemberOptions = (
	candidates: readonly StaffMemberView[],
	currentId: number | null,
	currentMember: StaffMemberView | null,
): StaffMemberOption[] => {
	const options: StaffMemberOption[] = candidates.map((candidate) => ({
		id: candidate.id,
		label: candidate.fullName,
	}));

	const isCurrentInCandidates =
		currentId !== null && candidates.some((candidate) => candidate.id === currentId);

	if (currentId !== null && !isCurrentInCandidates) {
		const label = currentMember !== null ? currentMember.fullName : `Membre du personnel n°${currentId}`;

		options.push({ id: currentId, label });
	}

	return [NONE_OPTION, ...options];
};

type Props = {
	readonly beneficiary: BeneficiaryView;
	readonly facilityId: number;
};

/**
 * Rattaching a member of staff never opens application access — these are employees of the
 * structure, with no account and no permission. That is why this panel is mounted under
 * `<WithPermission permission={[SUBJECT_WRITE, STAFF_READ]} mode="all">`: the members of the dossier
 * and the staff of the structure carry opposite consequences, so they carry different guards.
 *
 * `staffMembersStore.staffMembers` is populated by nobody upstream of this page (it belongs to the
 * Agencies/StaffMembers screen): this panel mounts its own `useStoreQuery`, guarded and keyed on
 * `organizationId`, exactly like `StaffMemberSidebar`. Candidates are restricted to `facilityId`
 * (`StaffMembersListView.forFacility`) — only a member of the dossier's own agency can be assigned —
 * then split into the two job functions (`nursingStaff` / `homeCareStaff`).
 */
const StaffAssignmentPanel: React.FC<Props> = ({ beneficiary, facilityId }) => {
	const dispatch = useDispatch();
	const { currentOrganization } = useCurrentOrganization();
	const organizationId = currentOrganization?.id ?? null;

	const {
		data: staffMembers,
		error,
		refetch,
	} = useStoreQuery({
		selector: (state) => state.staffMembersStore.staffMembers,
		stopOn: (value) => value !== null && value.organizationId === organizationId,
		query: () => {
			if (organizationId === null) {
				return Promise.resolve();
			}

			return dispatch(fetchStaffMembers(new FetchStaffMembers(organizationId)));
		},
		deps: [organizationId],
	});

	const [selectedNursingId, setSelectedNursingId] = React.useState<number | null>(
		beneficiary.nursingStaffMemberId,
	);
	const [selectedHomeCareId, setSelectedHomeCareId] = React.useState<number | null>(
		beneficiary.homeCareStaffMemberId,
	);
	const [isSubmitting, setIsSubmitting] = React.useState(false);

	const formId = React.useId();

	React.useEffect(() => {
		setSelectedNursingId(beneficiary.nursingStaffMemberId);
		setSelectedHomeCareId(beneficiary.homeCareStaffMemberId);
	}, [beneficiary.nursingStaffMemberId, beneficiary.homeCareStaffMemberId]);

	const handleSubmit = React.useCallback(() => {
		setIsSubmitting(true);

		dispatch(
			updateBeneficiaryStaffMembers(
				new UpdateBeneficiaryStaffMembers(
					facilityId,
					beneficiary.id,
					selectedNursingId,
					selectedHomeCareId,
				),
			),
		)
			.catch(ignoreHandledError)
			.finally(() => setIsSubmitting(false));
	}, [dispatch, facilityId, beneficiary.id, selectedNursingId, selectedHomeCareId]);

	const facilityStaffMembers = React.useMemo(
		() => (staffMembers !== null ? staffMembers.forFacility(facilityId) : null),
		[staffMembers, facilityId],
	);

	const nursingCandidates = React.useMemo(
		() => facilityStaffMembers?.nursingStaff() ?? [],
		[facilityStaffMembers],
	);

	const homeCareCandidates = React.useMemo(
		() => facilityStaffMembers?.homeCareStaff() ?? [],
		[facilityStaffMembers],
	);

	const nursingOptions = React.useMemo(
		() =>
			buildStaffMemberOptions(
				nursingCandidates,
				beneficiary.nursingStaffMemberId,
				staffMembers?.findById(beneficiary.nursingStaffMemberId) ?? null,
			),
		[nursingCandidates, beneficiary.nursingStaffMemberId, staffMembers],
	);

	const homeCareOptions = React.useMemo(
		() =>
			buildStaffMemberOptions(
				homeCareCandidates,
				beneficiary.homeCareStaffMemberId,
				staffMembers?.findById(beneficiary.homeCareStaffMemberId) ?? null,
			),
		[homeCareCandidates, beneficiary.homeCareStaffMemberId, staffMembers],
	);

	if (error !== null) {
		return (
			<section css={panelStyle}>
				<span css={eyebrowStyle}>Personnel de la structure</span>
				<ErrorState title="Impossible de charger le personnel" onRetry={refetch} />
			</section>
		);
	}

	if (staffMembers === null || facilityStaffMembers === null) {
		return (
			<section css={panelStyle}>
				<span css={eyebrowStyle}>Personnel de la structure</span>
				<Skeleton height="10rem" animation="wave" shape="rectangle" />
			</section>
		);
	}

	const isFacilityEmpty = facilityStaffMembers.list.length === 0;
	const hasNoNursingChoice = nursingOptions.length === 1;
	const hasNoHomeCareChoice = homeCareOptions.length === 1;

	return (
		<section css={panelStyle}>
			<span css={eyebrowStyle}>Personnel de la structure</span>
			<Form id={formId} onSubmit={handleSubmit} aria-label="Rattacher le personnel de la structure">
				<div css={formStyle}>
					<Dropdown<number | null>
						name="nursingStaffMember"
						label="Personnel soignant"
						options={nursingOptions}
						optionLabel="label"
						optionValue="id"
						disabled={hasNoNursingChoice}
						value={selectedNursingId}
						onChange={setSelectedNursingId}
						placeholder={
							hasNoNursingChoice
								? 'Aucun choix disponible'
								: 'Sélectionner un membre du personnel'
						}
					/>

					<Dropdown<number | null>
						name="homeCareStaffMember"
						label="Intervenant(e) à domicile"
						options={homeCareOptions}
						optionLabel="label"
						optionValue="id"
						disabled={hasNoHomeCareChoice}
						value={selectedHomeCareId}
						onChange={setSelectedHomeCareId}
						placeholder={
							hasNoHomeCareChoice
								? 'Aucun choix disponible'
								: 'Sélectionner un membre du personnel'
						}
					/>
					{isFacilityEmpty && (
						<Message
							severity={'warn'}
							content={
								'Aucun membre du personnel n’est enregistré pour cette structure. Pour rattacher un membre du personnel à ce dossier, il faut d’abord l’ajouter à la structure.'
							}
							css={fieldMarginStyles}
						/>
					)}
					<div css={actionsStyle}>
						<Button type="submit" label="Enregistrer" loading={isSubmitting} />
					</div>
				</div>
			</Form>
		</section>
	);
};

export default StaffAssignmentPanel;
